// Token-based DDL slot classifier — the replacement for the retired (deleted)
// string-walk `classifyDdlPositionKind` that lived in
// `intellisense/context/sqlClauseToPositionKind.ts` (plan19 Task 7, decision
// 8). PRD: GRA-8 (projection pivot). G11: pure text scan over the tokenised
// statement slice, NO web-tree-sitter — the legal projection-side body the
// future tree-sitter swap replaces behind the identical signature.
//
// `ddl-create-object-name` (the NEW object's name slot after a `CREATE …
// <object-type>` head, sibling file `editorContextSlot.ddl2.createName.ts`) is
// the one member of this family that is NOT a port — the retired classifier had
// no arm for it and the position fell through to `unknown`, flooding the list
// with columns/tables where only a namespace can go. It runs LAST so no ported
// answer changes.
//
// Further NON-PORT members joined later, each for a caret the retired
// classifier also left on `unknown`: `ddl-target-table-column` — a column list
// whose owning table is named AFTER the caret, either a
// `CREATE TRIGGER … UPDATE OF ▮` restriction list (sibling trigger file) or a
// `CREATE VIRTUAL TABLE … USING fts5(▮` module list — and the
// `PARTITION BY (▮` / bare `INDEX|KEY (▮` openers, which reuse the two
// column-list kinds the constraint leads already answer with. All are
// additive: they run after every ported check that could match the same token
// shape.
//
// `classifyDdlSlot` reproduces ALL NINE of the retired classifier's
// DdlPositionKind arms in ClauseToken form, at the SAME priority ordering, so
// the four resolvers that used to run the string-walk override (pgsql / mysql
// / oracle / sqlite) source these kinds from the slot seam instead. The token
// walk is string/comment/paren-safe by construction (the tokenizer already
// skips string literals + comments and balances parens over real tokens only)
// — where it diverges from the string-walk it is ONLY where the string-walk
// was provably wrong on string/comment/paren cases (see
// ddlPositionEquivalence.test.ts, the surviving corpus). PORT-FAITHFUL details
// (review S2/Q1): bare keyword tails the cursor is still attached to
// (`REFERENCES▮` / `USING▮` / `BEFORE▮`) do NOT fire — the retired regexes
// required `\s+` after the keyword, and firing mid-word would hide the
// keyword's own completion; the trigger-event vocabulary is exactly the
// retired INS/UPD/DEL prefix set (see editorContextSlot.ddl2.trigger.ts).
//
// The four constraint/reference kinds also have a narrower mssql-only
// recognizer in `editorContextSlot.ddl.ts` (`ddlConstraintSlot`); this file is
// the FULL cross-engine family (adds fk-target-first priority, GENERATED-AS,
// index-column, index-method, on-conflict, and — via the sibling trigger file
// — the two trigger slots), dispatched on the four flip engines' arms, leaving
// the mssql arm on its existing narrower recognizer.

import type { EditorContextSlotKind } from '../model/editorContext';
import type { ClauseToken } from './editorContextClause';
import { headTableVerb } from './editorContextSlot.ddl';
import {
  ddlConstraintLead,
  indexOrPartitionColumnListSlot,
  precededByIndexOn,
  precededByOnConflict,
  precededByReferencesTarget,
  precededByVirtualTableUsing,
} from './editorContextSlot.ddl2.paren';
import { createObjectNameSlot } from './editorContextSlot.ddl2.createName';
import { triggerSlot } from './editorContextSlot.ddl2.trigger';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

export type DdlSlotKind = Extract<
  EditorContextSlotKind,
  | 'ddl-index-column'
  | 'ddl-references-column-list'
  | 'ddl-create-table-expression'
  | 'ddl-alter-table-constraint-column-list'
  | 'ddl-fk-target'
  | 'ddl-index-method'
  | 'ddl-trigger-event'
  | 'ddl-target-table-column'
  | 'ddl-on-conflict-target'
  | 'ddl-create-trigger-timing'
  | 'ddl-create-object-name'
>;

/** Runtime set of the DDL family's slot kinds. Single source for the
 *  deriveContext named-clause override eligibility (review Q4 — replaces a
 *  `startsWith('ddl-')` prefix match so a future `ddl-*` slot kind does not
 *  silently gain override power without being added here). */
export const DDL_SLOT_KINDS: ReadonlySet<EditorContextSlotKind> = new Set<EditorContextSlotKind>([
  'ddl-index-column',
  'ddl-references-column-list',
  'ddl-create-table-expression',
  'ddl-alter-table-constraint-column-list',
  'ddl-fk-target',
  'ddl-index-method',
  'ddl-trigger-event',
  'ddl-target-table-column',
  'ddl-on-conflict-target',
  'ddl-create-trigger-timing',
  'ddl-create-object-name',
]);

/** The four flip engines (SqlEngine vocabulary) whose seam runs the DDL family
 *  — the engines whose resolvers used to run the retired string-walk override.
 *  Single source shared by `resolveSlotKind`'s dispatch and deriveContext's
 *  DDL-override gate (review Q4 — was duplicated in two vocabularies). mssql is
 *  deliberately absent (it never consumed the string-walk). */
export const DDL_FAMILY_SQL_ENGINES: ReadonlySet<string> = new Set([
  'postgres', 'mysql', 'oracle', 'sqlite',
]);

/**
 * Classify the DDL slot the cursor sits in (all nine kinds of the retired
 * string-walk classifier, token-based). Returns `null` when no DDL slot
 * matches (the caller keeps the clause result). `cursorOffset` distinguishes
 * a bare-keyword tail the cursor is attached to (`REFERENCES▮` — still typing
 * the keyword; ported null) from a separated slot (`REFERENCES ▮`).
 */
export function classifyDdlSlot(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): DdlSlotKind | null {
  if (tokens.length === 0) return null;

  // 0. bare `… REFERENCES ▮` FK-target tail — runs FIRST (even inside the
  //    enclosing CREATE TABLE `(`), matching the string-walk's tail-priority: a
  //    table-level `FOREIGN KEY (a) REFERENCES ▮` sits inside the CREATE TABLE
  //    paren but names the referenced TABLE, not in-progress columns.
  if (followsBareReferencesTail(tokens, cursorOffset)) return 'ddl-fk-target';

  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx >= 0) {
    // 1. REFERENCES <tbl>(▮ → the referenced table's column list.
    if (precededByReferencesTarget(tokens, lparenIdx)) return 'ddl-references-column-list';
    // 2. ON <tbl> [USING <method>] (▮ inside a CREATE [UNIQUE] INDEX.
    if (precededByIndexOn(tokens, lparenIdx) && headIsCreateIndex(tokens)) return 'ddl-index-column';
    // 3. ON CONFLICT (▮ inside an INSERT.
    if (precededByOnConflict(tokens, lparenIdx) && headHasInsert(tokens)) return 'ddl-on-conflict-target';
    // 3b. CREATE VIRTUAL TABLE … USING fts5(▮ — the FTS table's column list,
    //     whose source table is named in a `content='<tbl>'` option AFTER the
    //     caret (hence the look-ahead kind). SQLite-only construct.
    if (precededByVirtualTableUsing(tokens, lparenIdx)) return 'ddl-target-table-column';
    // 4. CHECK / PK / UNIQUE / FK / GENERATED AS (▮ inside CREATE or ALTER TABLE.
    const lead = ddlConstraintLead(tokens, lparenIdx);
    if (lead !== null) {
      const head = headTableVerb(tokens);
      if (head === 'create') return 'ddl-create-table-expression';
      if (head === 'alter' && lead === 'key-or-unique') return 'ddl-alter-table-constraint-column-list';
    }
    // 5. `PARTITION BY [<method>] (▮` and a bare `INDEX|KEY [name] (▮`
    //    secondary-index column list. Both name COLUMNS of the table the
    //    statement is defining or altering, so they answer with the same two
    //    kinds the constraint leads above do. They landed first as the
    //    mysql-only residual `isMysqlCreateTableExpressionSlot`
    //    (GRA8-ROLL-2), which is gated behind the ambiguity check and a
    //    `create` head — that left PG's `) PARTITION BY RANGE (▮` and MySQL's
    //    own `ALTER TABLE … ADD INDEX ix (a, ▮` on the `unknown` arm. Running
    //    them here makes the pair cross-engine and lets them win over a
    //    mis-named clause, like every other member of the family. The mysql
    //    residual stays in place; it now simply answers the same thing one
    //    step later and is unreachable for these two shapes.
    const openerKind = indexOrPartitionColumnListSlot(tokens);
    if (openerKind === 'ddl-create-table-expression') return openerKind;
    if (openerKind === 'ddl-alter-table-constraint-column-list') return openerKind;
    return null;
  }

  // 6. CREATE [UNIQUE] INDEX … USING ▮ → the access-method keyword slot.
  if (followsUsingMethodTail(tokens, cursorOffset) && headIsCreateIndex(tokens)) {
    return 'ddl-index-method';
  }

  // 7. CREATE TRIGGER … timing / event / `UPDATE OF <cols>` slots (sibling
  //    file — ported INS/UPD/DEL vocabulary + attached-tail rules).
  const trigger = triggerSlot(tokens, cursorOffset);
  if (trigger !== null) return trigger;

  // 8. CREATE … <object-type> ▮ — the NEW object's name slot (sibling file).
  //    Runs LAST so the ported trigger answers keep priority: the retired
  //    string-walk fired its TIMING slot on an ATTACHED trigger name too
  //    (`CREATE TRIGGER my_tr▮` → 'ddl-create-trigger-timing', pinned by the
  //    equivalence corpus), and that port-faithful answer must not flip. The
  //    bare `CREATE TRIGGER ▮` (no name typed yet) is untouched by the trigger
  //    slots and lands here.
  if (createObjectNameSlot(tokens, cursorOffset)) return 'ddl-create-object-name';
  return null;
}

/** True for a bare-word token (identifier or keyword) — the retired regexes'
 *  `[A-Za-z_][\w$]*` ident alternative matched keywords too. */
function isWordToken(t: ClauseToken | undefined): boolean {
  return t !== undefined && (t.kind === 'identifier' || t.kind === 'keyword');
}

/** `… REFERENCES ▮` (whitespace-separated) / `REFERENCES tbl▮` /
 *  `REFERENCES tbl.▮` at the tail (no enclosing `(`). */
function followsBareReferencesTail(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  const last = tokens[tokens.length - 1];
  // Bare keyword tail: fires only when whitespace separates the keyword from
  // the cursor — `REFERENCES▮` (still typing the keyword) stays null, matching
  // the retired regex's required `\s+`. An attached `REFERENCES` may still
  // count as a partial TARGET after an earlier REFERENCES via the walk below.
  if (upper(last) === 'REFERENCES' && last!.end < cursorOffset) return true;

  let i = tokens.length - 1;
  // Optional trailing partial target (identifier OR keyword-lexed bare word —
  // `REFERENCES order▮` fired in the retired regex); partials may be attached.
  if (isWordToken(tokens[i])) i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i >= 0 && tokens[i]!.kind === 'identifier') i -= 1;
  }
  return upper(tokens[i]) === 'REFERENCES';
}

/** `… USING ▮` (whitespace-separated) / `… USING <method-partial>▮` at the
 *  tail (no enclosing `(`). */
function followsUsingMethodTail(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  const last = tokens[tokens.length - 1];
  // Bare keyword tail requires separation (`USING▮` — still typing — stays
  // null, matching the retired regex's `\s+`).
  if (upper(last) === 'USING' && last!.end < cursorOffset) return true;
  // Attached method partial (`USING gi▮`) is fine; bare words only.
  if (isWordToken(last) && upper(tokens[tokens.length - 2]) === 'USING') return true;
  return false;
}

/** Head scan for a `CREATE [UNIQUE] INDEX` lead (NOT FULLTEXT/SPATIAL/BITMAP —
 *  those residuals stay with the per-engine recognizers, as with the retired
 *  string-walk's regex). */
function headIsCreateIndex(tokens: readonly ClauseToken[]): boolean {
  for (let i = 0; i < tokens.length; i += 1) {
    if (upper(tokens[i]) !== 'CREATE') continue;
    let j = i + 1;
    // `UNIQUE` (ANSI) and T-SQL's `CLUSTERED` / `NONCLUSTERED` may precede
    // INDEX, in either order (`CREATE UNIQUE NONCLUSTERED INDEX ix …`).
    while (INDEX_HEAD_MODIFIERS.has(upper(tokens[j]) ?? '')) j += 1;
    if (upper(tokens[j]) === 'INDEX') return true;
  }
  return false;
}

/** Modifiers legal between `CREATE` and `INDEX`. */
const INDEX_HEAD_MODIFIERS: ReadonlySet<string> = new Set([
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'COLUMNSTORE',
]);

/**
 * True at an index COLUMN-LIST caret — `CREATE [UNIQUE|CLUSTERED|…] INDEX ix
 * ON tbl (▮` or its `INCLUDE (▮` continuation. Exported because mssql does not
 * run `classifyDdlSlot` (it never consumed the retired string-walk override),
 * so `editorContextSlot`'s mssql arm dispatches this one recognizer directly —
 * the same pattern as the opener / create-name / trigger-event arms.
 */
export function indexColumnListSlot(tokens: readonly ClauseToken[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  return precededByIndexOn(tokens, lparenIdx) && headIsCreateIndex(tokens);
}

/** True when any token before the cursor is an INSERT keyword. */
function headHasInsert(tokens: readonly ClauseToken[]): boolean {
  return tokens.some((t) => upper(t) === 'INSERT');
}
